import React from "react";
import { readInlineData, useFragment } from "react-relay";
import { DateTimeDisplay } from "@components/datetime-display";
import { IdDisplayField } from "@components/id-display-field";
import { useTranslation } from "@i18n/hooks";
import { type orderHistoryTable_OrderHistoriesFragment$key } from "@relay/orderHistoryTable_OrderHistoriesFragment.graphql";
import { type orderHistoryTable_OrderHistoryFragment$key } from "@relay/orderHistoryTable_OrderHistoryFragment.graphql";
import { H2Span, P2Span } from "@themes/font-tags";
import { ORDER_HISTORIES_FRAGMENT, ORDER_HISTORY_FRAGMENT } from "./order-history-table.graphql";
import {
	awaitingAccountNoticeClass,
	headerClass,
	fieldWrapperClass,
} from "./order-history-table.styles";
import { type OrderHistoryTableProps } from "./order-history-table.types";
import { OrderHistoryEvent } from "../order-history-event";

export const OrderHistoryTable = ({
	orderHistoriesFragmentRef,
	hasPurchaser,
}: OrderHistoryTableProps) => {
	const { t } = useTranslation("common");
	const orders = useFragment<orderHistoryTable_OrderHistoriesFragment$key>(
		ORDER_HISTORIES_FRAGMENT,
		orderHistoriesFragmentRef,
	);

	const history = readInlineData<orderHistoryTable_OrderHistoryFragment$key>(
		ORDER_HISTORY_FRAGMENT,
		orders.node?.history ?? null,
	);

	if (!history) {
		return (
			<div>
				<H2Span>{t("order.noHistoryAvailable")}</H2Span>
			</div>
		);
	}

	// LinkOrderToAccount now records its own dated "OrderClaimed" event and syncs
	// history.status to Purchased on claim, so newly-claimed orders won't hit this branch.
	// This stays as a fallback for orders claimed before that fix shipped: their history.status
	// is permanently frozen at PurchasedAwaitingAccount (no backfill), so hasPurchaser is still
	// needed to phrase the notice correctly for those legacy claimed orders.
	const isAwaitingAccount = history.status === "PurchasedAwaitingAccount";

	return (
		<div>
			<header className={headerClass}>
				<div className={fieldWrapperClass}>
					<P2Span>{t("table.id")}</P2Span>
					<IdDisplayField id={history.id} />
				</div>
				<div className={fieldWrapperClass}>
					<P2Span>{t("order.creationDate")}</P2Span>
					<DateTimeDisplay value={history.createdAt} hideTimezone />
				</div>
				<div className={fieldWrapperClass}>
					<P2Span>{t("order.status")}</P2Span>
					<div>{t(`orderHistoryStatus.${history.status}`)}</div>
				</div>
			</header>
			{isAwaitingAccount && (
				<div className={awaitingAccountNoticeClass}>
					{hasPurchaser
						? t("order.wasAwaitingAccountNotice")
						: t("order.awaitingAccountNotice")}
				</div>
			)}
			{history.event.map((event, index) => {
				return (
					<OrderHistoryEvent
						key={`${index}-${event.kind}`}
						orderHistoryFragmentRef={event}
					/>
				);
			})}
		</div>
	);
};
